import { prompts } from '@thecompaniesapi/shared/client'
import { slugify } from '@thecompaniesapi/string-helpers'
import dayjs from 'dayjs'
import Fuse from 'fuse.js'
import type { Action, AskPromptResponse, CleanupPromptResponse, EnrichPromptResponse, PaginationMeta, Prompt, PromptContext, PromptLoadingState, PromptResponse, SearchPromptResponse, SimilarPromptResponse } from '@thecompaniesapi/shared/client'

export const usePromptStore = defineStore('prompt', () => {
  const { height: windowHeight } = useWindowSize()
  const { isOverlayOpened } = useOverlays()
  const { synchronizeUrl } = useSegmentation()
  const { t } = useI18n()
  const { y: scrollY } = useWindowScroll()
  const actionsStore = useActionsStore()
  const api = useApi()
  const localePath = useLocalePath()
  const route = useRoute()
  const router = useRouter()

  const loadingStates: PromptLoadingState[] = [
    'analyzing-context',
    'fetching-companies',
    'fetching-context',
    'finding-feature',
    'finding-segmentation',
    'finding-target',
    'loading',
    'resolving-company-names',
    'resolving-data-schema',
    'validating-segmentation',
  ]

  const historyStorage = useLocalStorage<{
    meta: PaginationMeta
    prompt: Prompt
    response: PromptResponse
  }[]>(
    'tca-prompt-history',
    [],
    {
      serializer: {
        read: JSON.parse,
        write: JSON.stringify,
      },
    },
  )

  // Configuration for positioning
  const MIN_SCROLLABLE_AREA = 200
  const MIN_THRESHOLD = 120
  const scrollHeight = ref(0)

  const completionsForce = ref<boolean | undefined>()
  const isFocused = ref(false)
  const loading = ref<false | string>(false)
  const loadingState = ref<PromptLoadingState | undefined>()

  const prompt = ref<string | undefined>()
  const selectedCompletion = ref<InputCompletion | undefined>()
  const selectedIndex = ref(-1)
  const submitting = ref(false)

  const promptResponse = ref<{
    meta: PaginationMeta
    prompt: Prompt
    response: PromptResponse
  }>()

  const loadingStateIndex = computed(() => {
    return loadingStates.indexOf(loadingState.value || 'loading')
  })

  const promptKey = computed(() => {
    return slugify(prompt?.value || '')
  })

  const hasList = computed(() => {
    return route.params.listId
  })

  const currentResponse = computed<PromptResponse | undefined>(() => {
    if (promptResponse.value?.response && !promptResponse.value.response.error) {
      return promptResponse.value.response
    }
  })

  const currentAction = computed<Action | undefined>(() => {
    if ((currentResponse.value as AskPromptResponse | CleanupPromptResponse | EnrichPromptResponse)?.action) {
      return (currentResponse.value as AskPromptResponse | CleanupPromptResponse | EnrichPromptResponse).action as any as Action & { promptId: number }
    }
  })

  // Current feature selected in prompt
  const currentFeature = computed(() => {
    if (selectedCompletion.value?.key) {
      return prompts.features.find(feature => feature === selectedCompletion.value?.key)
    }
  })

  // Current context for product prompt
  const promptContext = computed<PromptContext>(() => {
    // Landing page
    if (String(route.name).startsWith('index')) {
      return 'landing'
    }

    // List analytics page
    if (String(route.name).startsWith('companies-lists-listId-analytics')) {
      return 'analytics'
    }

    // Lists page
    if (String(route.name).startsWith('companies-lists-listId')) {
      return 'list'
    }

    // Enrichment page
    if (String(route.name).startsWith('companies-enrichment')) {
      return 'enrichment'
    }

    // Similarity page
    if (String(route.name).startsWith('companies-similarity')) {
      return 'similarity'
    }

    // Companies search page
    if (String(route.name).startsWith('companies')) {
      return 'companies'
    }

    // Fallback on companies as it's widest context available
    return 'companies'
  })

  // Completions
  const completions = computed(() => {
    const featureCompletions: InputCompletion[] = [
      {
        color: getFeatureColor('search'),
        description: t('prompt.completions.search.description'),
        key: 'search',
        label: t('prompt.completions.search.label'),
        onSelect: (completion) => {
          selectedCompletion.value = completion
          prompt.value = ''
        },
      },
      {
        color: getFeatureColor('similar'),
        description: t('prompt.completions.similar.description'),
        key: 'similar',
        label: t('prompt.completions.similar.label'),
        onSelect: (completion) => {
          selectedCompletion.value = completion
          prompt.value = ''
        },
      },
      {
        color: getFeatureColor('enrich'),
        description: t('prompt.completions.enrich.description'),
        key: 'enrich',
        label: t('prompt.completions.enrich.label'),
        onSelect: (completion) => {
          selectedCompletion.value = completion
          prompt.value = ''
        },
      },
      {
        color: getFeatureColor('ask'),
        description: t('prompt.completions.ask.description'),
        key: 'ask',
        label: t('prompt.completions.ask.label'),
        onSelect: (completion) => {
          selectedCompletion.value = completion
          prompt.value = ''
        },
      },
      {
        color: getFeatureColor('cleanup'),
        description: t('prompt.completions.cleanup.description'),
        key: 'cleanup',
        label: t('prompt.completions.cleanup.label'),
        onSelect: (completion) => {
          selectedCompletion.value = completion
          prompt.value = ''
        },
      },
    ]

    let result: InputCompletion[] = []

    // Handle @ completions
    if (prompt.value?.startsWith('@') && !selectedCompletion.value) {
      result = featureCompletions

      // If the prompt is only @, select the first completion
      if (prompt.value?.length === 1) {
        selectedIndex.value = 0
      }
    }

    // If completions are forced, show history completions
    if (completionsForce.value /* || (prompt.value && !prompt.value.startsWith('@')) */) {
      result = historyStorage.value.map(
        (item) => {
        // If feature is available, select it
          let featureCompletion: InputCompletion | undefined

          if (item.prompt.feature) {
            featureCompletion = featureCompletions.find(compl => compl.key === item.prompt.feature)
          }

          return {
            color: featureCompletion?.color || 'gray',
            createdAt: item.prompt.createdAt,
            description: item.prompt.prompt,
            feature: item.prompt.feature,
            key: `history:${item.prompt.promptKey}`,
            label: item.prompt.promptKey,
            onSelect: () => {
              prompt.value = item.prompt.prompt

              if (featureCompletion) {
                selectedCompletion.value = featureCompletion
              }
            },
            updatedAt: item.prompt.updatedAt,
          }
        },
      )
    }

    // Filter completions based on prompt using Fuse.js for fuzzy searching
    if (prompt.value && prompt.value !== '@') {
      const promptLower = prompt.value.toLowerCase()

      const fuse = new Fuse<InputCompletion>(
        result,
        {
          includeScore: true,
          keys: [
            'label',
            'description',
            'key',
          ],
          shouldSort: true,
          sortFn: (aItem, bItem) => {
            const aPrompt = aItem.item as InputCompletion
            const bPrompt = bItem.item as InputCompletion

            const aDate = dayjs(aPrompt.updatedAt || aPrompt.createdAt)
            const bDate = dayjs(bPrompt.updatedAt || bPrompt.createdAt)

            return bDate.diff(aDate)
          },
          threshold: 0.3,
        },
      )

      const searchResults = fuse.search(promptLower.startsWith('@') ? promptLower.slice(1) : promptLower)

      // Convert Fuse results back to our completion format
      result = searchResults.map(searchResult => searchResult.item)
    }

    if (!hasList.value) {
      result = result.filter((item) => {
        if (item.key === 'cleanup' || item.key === 'ask') {
          return false
        }

        return true
      })
    }

    return result.slice(0, 10)
  })

  // Check if page has layout
  const pageHasPrompt = computed(() => {
    if (
      !String(route.name).startsWith('companies')
      || String(route.name).startsWith('companies-enrichment')
      || String(route.name).startsWith('companies-similarity')
      || isOverlayOpened.value
    ) {
      return false
    }

    return true
  })

  // Check if page is scrollable and has enough scroll space
  const hasScroll = computed(() => {
    const scrollableArea = scrollHeight.value - windowHeight.value

    return scrollableArea > MIN_SCROLLABLE_AREA
  })

  // Enhanced visibility logic with better threshold handling
  const isVisible = computed(() => {
    if (!pageHasPrompt.value) {
      return false
    }

    if (!scrollHeight.value || !windowHeight) {
      return true
    }

    // If there's not enough scroll space, treat it as a non-scrolling page
    if (!hasScroll.value) {
      return true
    }

    const distanceFromBottom = scrollHeight.value - (scrollY.value + windowHeight.value)

    return distanceFromBottom > MIN_THRESHOLD
  })

  const addToHistory = (
    response: {
      meta: PaginationMeta
      prompt: Prompt
      response: PromptResponse
    },
  ) => {
    const key = response.prompt.promptKey
    const existing = historyStorage.value.find(item => item.prompt.promptKey === key || item.prompt.prompt === response.prompt.prompt)

    // Skip if existing
    if (existing) {
      if (existing.meta) {
        existing.prompt.updatedAt = response.prompt.updatedAt
        existing.prompt.createdAt = response.prompt.createdAt
      }

      return
    }

    // Add to history
    historyStorage.value.push(response)
  }

  const removeFromHistory = (key: string) => {
    historyStorage.value = historyStorage.value.filter(item => `history:${item.prompt.promptKey}` !== key && item.prompt.promptKey !== key)
  }

  const fetch = async () => {
    if (!prompt.value?.trim()?.length) {
      return
    }

    loading.value = promptKey.value

    try {
      const response = await api.prompts.processForProduct({
        context: promptContext.value,
        feature: currentFeature.value,
        listId: route.params.listId ? Number(route.params.listId) : undefined,
        prompt: prompt.value,
      })

      if (response.data) {
        promptResponse.value = response.data

        // Add to history is response is valid
        if (response?.data?.prompt?.promptKey) {
          addToHistory(response.data)
        }

        return response.data
      }
    }
    catch (error) {
      console.error('Prompt fetch failed:', error)
    }
    finally {
      loading.value = false
      loadingState.value = undefined
    }
  }

  const process = async () => {
    submitting.value = true

    // If there is a current action, handle it
    if (currentAction.value) {
      try {
        const actions = await actionsStore.requestAction({
          domains: currentAction.value.data?.domains,
          fields: currentAction.value.data?.fields,
          job: currentAction.value.data?.job,
          listId: currentAction.value.listId,
          promptId: currentAction.value.promptId,
          query: currentAction.value.data?.query,
          question: currentAction.value.data?.question,
          type: 'jobs:request',
        })

        const action = actions?.[0]

        if (action?.id) {
          if (action?.listId) {
            router.push(
              localePath({
                name: 'companies-lists-listId',
                query: { action: `${action.id}` },
              }),
            )
          }
          else {
            router.push(
              localePath({
                name: 'companies',
                query: { action: `${action.id}` },
              }),
            )
          }
        }

        reset()
      }
      catch (error) {
        console.error('Prompt process failed:', error)

        // Restore prompt for retry
        prompt.value = currentAction.value?.data?.question || ''

        reset()
      }

      // Not submitting
      submitting.value = false

      return
    }

    try {
      // Fetch response
      const newResponse = await fetch()

      // If no response is available after fetching, return
      if (!newResponse) {
        return
      }

      const feature = newResponse?.prompt?.feature

      // Similar response
      if (feature === 'similar' && (newResponse?.response as SimilarPromptResponse)?.domains) {
        router.push(
          localePath({
            name: 'companies-similarity',
            query: { domains: (newResponse?.response as SimilarPromptResponse)?.domains },
          }),
        )

        promptResponse.value = undefined

        return
      }

      // Search response
      if (feature === 'search' && (newResponse?.response as SearchPromptResponse)?.query) {
        synchronizeUrl({
          domains: [],
          query: (newResponse?.response as SearchPromptResponse)?.query || [],
          route: route.params.listId
            ? {
                name: route.name,
                params: { listId: `${route.params.listId}` },
              }
            : { name: 'companies' },
        })

        promptResponse.value = undefined
      }

      if (feature === 'search' && (newResponse?.response as SearchPromptResponse)?.domains?.length) {
        router.push(
          localePath({
            name: route.name,
            query: {
              'domain': (newResponse?.response as SearchPromptResponse)?.domains?.[0],
              'domains-group': ((newResponse?.response as SearchPromptResponse)?.domains?.length || 0) > 1
                ? (newResponse?.response as SearchPromptResponse)?.domains?.join(',')
                : undefined,
            },
          }),
        )

        promptResponse.value = undefined
      }

      if (feature === 'ask' && (newResponse?.response as AskPromptResponse)?.answer && (newResponse?.response as AskPromptResponse)?.domain) {
        router.push(
          localePath({
            name: route.name,
            query: {
              domain: (newResponse?.response as AskPromptResponse)?.domain,
              tab: 'ask',
            },
          }),
        )

        promptResponse.value = undefined
      }
    }
    catch (error) {
      console.error('Prompt process failed:', error)
    }
    finally {
      // Reset prompt
      prompt.value = ''
      submitting.value = false
    }
  }

  const reset = () => {
    completionsForce.value = undefined
    loading.value = false
    prompt.value = ''
    promptResponse.value = undefined
    selectedCompletion.value = undefined
    submitting.value = false
  }

  const onCompletionSelected = (_completion: InputCompletion) => {
    // Close completions force if any is selected
    completionsForce.value = undefined
  }

  onMounted(() => {
    useResizeObserver(
      document?.body,
      () => (scrollHeight.value = document?.documentElement?.scrollHeight),
    )
  })

  useEvent(
    'user',
    'prompt:step',
    (data) => {
      if (data.promptKey === loading.value) {
        loadingState.value = data.step
      }
    },
  )

  useEvent(
    'visitor',
    'prompt:step',
    (data) => {
      if (data.promptKey === loading.value) {
        loadingState.value = data.step
      }
    },
  )

  return {
    addToHistory,
    completions,
    completionsForce,
    currentAction,
    currentFeature,
    currentResponse,
    fetch,
    hasScroll,
    isFocused,
    isVisible,
    loading,
    loadingState,
    loadingStateIndex,
    loadingStates,
    onCompletionSelected,
    pageHasPrompt,
    process,
    prompt,
    promptContext,
    promptKey,
    promptResponse,
    removeFromHistory,
    reset,
    selectedCompletion,
    selectedIndex,
    submitting,
  }
})
